'use client';

import { c as _c } from "react/compiler-runtime";
import { InfoTooltip } from '@/comps/info-tooltip';
import { Skeleton } from '@/comps/primitives/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/comps/primitives/table';
import { MODEL_SAVINGS_NEGATIVE_COLOR, MODEL_SAVINGS_POSITIVE_COLOR, SAVINGS_CALCULATION_TABLE_TOOLTIPS, SAVINGS_DETAILS_SECTION_TOOLTIPS } from '@/domains/pages/model-savings/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { TierBadge } from '@/domains/pages/model-savings/components/tier-badge';
import { ModelProviderIcon } from '@/domains/pages/model-savings/components/model-provider-icon';
import type { SavingsCalculationRow } from '@/domains/pages/model-savings/types';
import { formatAnnualSpend } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';

/** Column label plus the same (i) affordance used next to card titles. */
function CalculationTableHead(t0) {
  const $ = _c(12);
  const {
    title,
    tooltip,
    align: t1
  } = t0;
  const align = t1 === undefined ? "left" : t1;
  const t2 = align === "right" ? "text-right" : undefined;
  const t3 = align === "right" && "justify-end";
  let t4;
  if ($[0] !== t3) {
    t4 = cn("flex items-center", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = `${title} column description`;
  let t6;
  if ($[2] !== t5 || $[3] !== tooltip) {
    t6 = <InfoTooltip content={tooltip} label={t5} />;
    $[2] = t5;
    $[3] = tooltip;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t4 || $[6] !== t6 || $[7] !== title) {
    t7 = <div className={t4}>{title}{t6}</div>;
    $[5] = t4;
    $[6] = t6;
    $[7] = title;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t7) {
    t8 = <TableHead className={t2}>{t7}</TableHead>;
    $[9] = t2;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}
type CalculationTableProps = {
  rows: SavingsCalculationRow[];
  totalActualSpend: number;
  totalOptimalSpend: number;
  totalDelta: number;
  isLoading?: boolean;
};
export function CalculationTable(t0) {
  const $ = _c(14);
  const {
    rows,
    totalActualSpend,
    totalOptimalSpend,
    totalDelta,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isTotalSaving = totalDelta < 0;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="mb-3 flex items-center text-[10px] font-medium tracking-wider text-muted-foreground uppercase">Annualized savings opportunity calculation<InfoTooltip content={SAVINGS_DETAILS_SECTION_TOOLTIPS.calculation} label="How the savings calculation works" /></p>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TableHeader><TableRow><CalculationTableHead title="Model" tooltip={SAVINGS_CALCULATION_TABLE_TOOLTIPS.model} /><CalculationTableHead title="Tier" tooltip={SAVINGS_CALCULATION_TABLE_TOOLTIPS.tier} /><CalculationTableHead title="Actual" tooltip={SAVINGS_CALCULATION_TABLE_TOOLTIPS.actual} align="right" /><CalculationTableHead title="Optimal" tooltip={SAVINGS_CALCULATION_TABLE_TOOLTIPS.optimal} align="right" /><CalculationTableHead title="Change" tooltip={SAVINGS_CALCULATION_TABLE_TOOLTIPS.change} align="right" /></TableRow></TableHeader>;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isLoading || $[3] !== rows) {
    t4 = isLoading ? Array.from({
      length: 3
    }).map(_temp) : rows.map(_temp2);
    $[2] = isLoading;
    $[3] = rows;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isLoading || $[6] !== isTotalSaving || $[7] !== totalActualSpend || $[8] !== totalDelta || $[9] !== totalOptimalSpend) {
    t5 = !isLoading ? <TableRow className="border-t border-t-lightborder font-semibold"><TableCell colSpan={2}>Total</TableCell><TableCell className={cn("text-right", AIFI_DATA_NUMBER_CLASS)}>{formatAnnualSpend(totalActualSpend)}</TableCell><TableCell className={cn("text-right", AIFI_DATA_NUMBER_CLASS)}>{formatAnnualSpend(totalOptimalSpend)}</TableCell><TableCell className={cn("text-right", AIFI_DATA_NUMBER_CLASS)} style={{
        color: isTotalSaving ? MODEL_SAVINGS_POSITIVE_COLOR : MODEL_SAVINGS_NEGATIVE_COLOR
      }}>{isTotalSaving ? "-" : "+"}{formatAnnualSpend(Math.abs(totalDelta))}</TableCell></TableRow> : null;
    $[5] = isLoading;
    $[6] = isTotalSaving;
    $[7] = totalActualSpend;
    $[8] = totalDelta;
    $[9] = totalOptimalSpend;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div>{t2}<Table>{t3}<TableBody>{t4}{t5}</TableBody></Table></div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp2(row) {
  const delta = row.optimalSpend - row.actualSpend;
  const isSaving = delta < 0;
  return <TableRow key={`${row.provider ?? "unknown"}-${row.modality ?? "text"}-${row.tier}`}><TableCell><span className="inline-flex items-center gap-2"><ModelProviderIcon provider={row.provider} modelName={row.modelName} />{row.modelName}</span></TableCell><TableCell><TierBadge tier={row.tier} /></TableCell><TableCell className="text-right"><div className={cn("font-medium", AIFI_DATA_NUMBER_CLASS)}>{formatAnnualSpend(row.actualSpend)}</div><div className="text-[10px] text-muted-foreground">{formatScore(row.actualMixPercent, {
          decimals: 0
        })}%</div></TableCell><TableCell className="text-right"><div className={cn("font-medium", AIFI_DATA_NUMBER_CLASS)}>{formatAnnualSpend(row.optimalSpend)}</div><div className="text-[10px] text-muted-foreground">{formatScore(row.optimalMixPercent, {
          decimals: 0
        })}%</div></TableCell><TableCell className={cn("text-right font-semibold", AIFI_DATA_NUMBER_CLASS)} style={{
      color: isSaving ? MODEL_SAVINGS_POSITIVE_COLOR : MODEL_SAVINGS_NEGATIVE_COLOR
    }}>{isSaving ? "-" : "+"}{formatAnnualSpend(Math.abs(delta))}</TableCell></TableRow>;
}
function _temp(_, index) {
  return <TableRow key={index}><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>;
}